import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, CheckCircle2, FileText, HelpCircle, IdCard, MessageSquare, Phone, Shield, Wallet } from "lucide-react";
import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { fetchPortal, portalConsent, portalRequest, portalUpload, type PortalView } from "../api";
import { PoweredByStatic } from "../components/PoweredBy";
import { translate, type Locale } from "../i18n";
import { applyBrand, fileToBase64, formatPhone, money, shortDate } from "../theme";

/**
 * The customer's private page. No login: the token in the URL is the key, issued from the customer file. Everything
 * the customer does here becomes a task for their owner at the agency. Phone-first, bilingual, branded.
 */

type Kind = "id_card" | "proof" | "change" | "question" | "callback";
const KINDS: Array<{ id: Kind; icon: typeof IdCard }> = [{ id: "id_card", icon: IdCard }, { id: "proof", icon: Shield }, { id: "change", icon: FileText }, { id: "question", icon: HelpCircle }, { id: "callback", icon: Phone }];

export function PortalScreen() {
  const { token = "" } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["portal", token], queryFn: () => fetchPortal(token), retry: false });
  const [locale, setLocale] = useState<Locale>(() => (navigator.language.toLowerCase().startsWith("es") ? "es" : "en"));
  const t = (k: string) => translate(locale, k);
  const [kind, setKind] = useState<Kind | null>(null);
  const [details, setDetails] = useState("");
  const [policyId, setPolicyId] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState("");
  const d = q.data;

  useEffect(() => {
    if (d) { applyBrand(d.agency.brand); document.title = `${d.agency.displayName} · ${t("portal.title")}`; setLocale(d.customer.locale); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d?.agency.displayName]);
  useEffect(() => { document.documentElement.lang = locale; }, [locale]);

  const field = "w-full min-h-[52px] rounded-2xl border border-ink/15 bg-white px-4 text-base text-ink placeholder:text-ink/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30";
  const button = "inline-flex min-h-[52px] items-center justify-center gap-2 rounded-2xl bg-primary px-5 text-base font-bold text-white shadow-sm hover:bg-primary-dark disabled:opacity-50";

  const submitRequest = async (e: FormEvent) => {
    e.preventDefault();
    if (!kind) return;
    setBusy(true); setError("");
    try { await portalRequest(token, { kind, details, policyId: policyId || undefined, locale }); setSent(true); setKind(null); setDetails(""); void qc.invalidateQueries({ queryKey: ["portal", token] }); }
    catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  };
  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true); setError(""); setUploaded(false);
    try {
      const base64 = await fileToBase64(f);
      await portalUpload(token, { title: uploadTitle.trim() || f.name, contentType: f.type || "application/octet-stream", base64 });
      setUploaded(true); setUploadTitle("");
    } catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  };
  const consent = async (agree: boolean) => {
    setBusy(true); setError("");
    try { await portalConsent(token, { agree, locale, consentText: t("portal.texting.consent") }); void qc.invalidateQueries({ queryKey: ["portal", token] }); }
    catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-surface text-ink font-body">
      <header className="bg-ink text-white">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3">
            {d ? <img src="/api/public/logo" alt="" className="h-9 w-9 rounded-lg bg-white object-contain p-0.5" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} /> : null}
            <div><p className="font-bold leading-tight">{d?.agency.displayName ?? "…"}</p><p className="text-[11px] text-white/70">{t("portal.title")}</p></div>
          </div>
          <div className="flex overflow-hidden rounded-lg border border-white/30 text-xs font-bold">
            {(["en", "es"] as const).map((l) => <button key={l} type="button" onClick={() => setLocale(l)} className={`px-3 py-1.5 ${locale === l ? "bg-white text-ink" : "text-white"}`}>{l.toUpperCase()}</button>)}
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-2xl space-y-4 px-4 py-5">
        {q.isError ? <p className="rounded-2xl bg-red-50 p-4 text-red-800">{t("portal.invalid")}</p> : null}
        {!d && !q.isError ? <p className="text-ink/55">…</p> : null}
        {d ? (
          <>
            <section>
              <h1 className="text-2xl font-bold">{t("portal.hello").replace("{name}", d.customer.firstName)}</h1>
              <p className="text-ink/65">{d.customer.agentName ? `${t("portal.agent")}: ${d.customer.agentName} · ` : ""}{formatPhone(d.agency.contact.phone)}</p>
            </section>

            <section className="rounded-3xl bg-white p-4 shadow-card">
              <h2 className="text-lg font-bold">{t("portal.policies")}</h2>
              {d.policies.length === 0 ? <p className="mt-1 text-ink/55">{t("portal.noPolicies")}</p> : (
                <ul className="mt-2 divide-y divide-ink/5">
                  {d.policies.map((p) => (
                    <li key={p.id} className="flex items-center justify-between gap-3 py-2.5">
                      <div><p className="font-semibold">{p.carrier} · {p.line.replace(/_/g, " ")}</p><p className="text-xs text-ink/55">{p.policyNumber}</p></div>
                      <div className="text-right text-sm"><p className="font-semibold capitalize">{p.status.replace(/_/g, " ")}</p><p className="text-xs text-ink/55">{t("portal.expires")} {shortDate(`${p.expirationDate}T12:00:00`, locale)}</p></div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="rounded-3xl bg-white p-4 shadow-card">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {KINDS.map(({ id, icon: Icon }) => (
                  <button key={id} type="button" onClick={() => { setKind(kind === id ? null : id); setSent(false); }} className={`flex min-h-[72px] flex-col items-center justify-center gap-1 rounded-2xl border px-2 text-center text-sm font-semibold ${kind === id ? "border-primary bg-primary/10 text-primary-dark" : "border-ink/10 hover:border-primary/50"}`}>
                    <Icon className="h-5 w-5" /> {t(`portal.request.${id}`)}
                  </button>
                ))}
              </div>
              {kind ? (
                <form onSubmit={submitRequest} className="mt-3 space-y-2">
                  {d.policies.length > 1 ? <select className={field} value={policyId} onChange={(e) => setPolicyId(e.target.value)}>{d.policies.map((p) => <option key={p.id} value={p.id}>{p.carrier} {p.line.replace(/_/g, " ")} {p.policyNumber}</option>)}</select> : null}
                  <textarea className={`${field} min-h-[96px] py-3`} placeholder={t("portal.request.details")} value={details} onChange={(e) => setDetails(e.target.value)} />
                  <button type="submit" className={button} disabled={busy}><MessageSquare className="h-5 w-5" />{t("portal.request.send")}</button>
                </form>
              ) : null}
              {sent ? <p className="mt-3 flex items-center gap-2 rounded-2xl bg-primary/10 p-3 text-sm font-semibold text-primary-dark"><CheckCircle2 className="h-5 w-5" />{t("portal.request.sent")}</p> : null}
              {d.openRequests.length ? <p className="mt-3 text-xs text-ink/55">{t("portal.requests")}: {d.openRequests.map((r) => r.kind.replace(/_/g, " ")).join(", ")}</p> : null}
            </section>

            <section className="rounded-3xl bg-white p-4 shadow-card">
              <h2 className="text-lg font-bold">{t("portal.upload")}</h2>
              <p className="text-sm text-ink/60">{t("portal.upload.hint")}</p>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <input className={field} placeholder={t("portal.upload.title")} value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)} />
                <label className={`${button} cursor-pointer`}><Camera className="h-5 w-5" />{t("portal.upload.send")}<input type="file" accept="image/*,.pdf" className="hidden" onChange={pick} disabled={busy} /></label>
              </div>
              {uploaded ? <p className="mt-2 flex items-center gap-2 text-sm font-semibold text-primary-dark"><CheckCircle2 className="h-5 w-5" />{t("portal.upload.done")}</p> : null}
            </section>

            <section className="rounded-3xl bg-white p-4 shadow-card">
              <h2 className="flex items-center gap-2 text-lg font-bold"><Wallet className="h-5 w-5" />{t("portal.pay")}</h2>
              <p className="text-sm text-ink/60">{t("portal.pay.hint")}</p>
              {d.agency.offices.map((o) => <p key={o.name} className="mt-1 text-sm"><span className="font-semibold">{o.name}</span> · {o.address} · {formatPhone(o.phone)}</p>)}
              {d.payments.length ? <><p className="mt-3 text-xs font-bold uppercase tracking-wide text-ink/50">{t("portal.payments")}</p><ul className="text-sm">{d.payments.map((p) => <li key={p.receiptNumber} className="flex justify-between py-1"><span>{p.receiptNumber} · {shortDate(p.takenAt, locale)}</span><span className="font-semibold">{money(p.amountCents)}</span></li>)}</ul></> : null}
            </section>

            <section className="rounded-3xl bg-white p-4 shadow-card">
              <h2 className="text-lg font-bold">{t("portal.texting")}</h2>
              <p className="text-sm text-ink/60">{d.smsConsent ? t("portal.texting.on") : t("portal.texting.off")}</p>
              {!d.smsConsent ? <p className="mt-2 text-xs text-ink/55">{t("portal.texting.consent")}</p> : null}
              <button type="button" onClick={() => consent(!d.smsConsent)} disabled={busy} className={`mt-2 ${d.smsConsent ? "inline-flex min-h-[44px] items-center rounded-2xl border border-ink/15 px-4 text-sm font-semibold" : button}`}>{d.smsConsent ? t("portal.texting.disable") : t("portal.texting.enable")}</button>
            </section>
            {error ? <p className="rounded-2xl bg-red-50 p-3 text-sm text-red-800">{error}</p> : null}
            <p className="text-center text-xs text-ink/45">{t("portal.footer")}</p>
          </>
        ) : null}
        <PoweredByStatic locale={locale} className="mx-auto mt-6" />
      </main>
    </div>
  );
}

export type { PortalView };
